import React, { useEffect, useState } from 'react';
import api from '../api';
import PageHeader from '../components/PageHeader';
import BotSelector from '../components/BotSelector';
import { Avatar, Bar, Ic, Spinner, StatCard, fmtNum, useToast } from '../components/ui';

const FIELD_LABELS = {
  xp: 'XP', level: 'Level', points: 'Puan', coins: 'Coin',
  streak: 'Seri', total_correct: 'Doğru', games_won: 'Galibiyet',
  created_at: 'Kayıt', last_seen: 'Son görülme',
};
const LB_FIELDS = ['xp', 'level', 'points', 'coins', 'streak', 'total_correct', 'games_won'];
const MEDAL = ['#f5c542', '#c0c8d2', '#d39a62'];
const TEMPLATES = [
  { name: 'Oyun botu', icon: 'game', levels: [{ level: 1, name: 'Çaylak', required_xp: 0, badge: '🌱' }, { level: 2, name: 'Acemi', required_xp: 100, badge: '⚡' }, { level: 3, name: 'Tecrübeli', required_xp: 300, badge: '🔥' }, { level: 4, name: 'Uzman', required_xp: 700, badge: '💎' }, { level: 5, name: 'Efsane', required_xp: 1500, badge: '👑' }] },
  { name: 'Bilgi yarışması', icon: 'sparkle', levels: [{ level: 1, name: 'Başlangıç', required_xp: 0, badge: '📚' }, { level: 2, name: 'Öğrenci', required_xp: 50, badge: '🎓' }, { level: 3, name: 'Uzman', required_xp: 200, badge: '🧠' }, { level: 4, name: 'Dahi', required_xp: 500, badge: '⭐' }, { level: 5, name: 'Üstad', required_xp: 1000, badge: '🏆' }] },
];
const EMPTY_LEVEL = { level: 1, name: '', required_xp: 0, badge: '⭐', reward_message: '' };

const CSS = `
.pf-wrap { display:grid; grid-template-columns:minmax(0,1fr); gap:16px; align-items:start; }
.pf-wrap.has { grid-template-columns:minmax(0,1fr) 360px; }
.pf-side { position:sticky; top:16px; }
.pf-meta { display:flex; gap:14px; flex-shrink:0; text-align:right; }
.pf-meta .m b { display:block; font-size:15px; font-weight:700; line-height:1.2; }
.pf-meta .m span { font-size:11.5px; color:var(--text3); }
.pf-lvl { display:inline-flex; align-items:center; justify-content:center; min-width:34px; height:22px; padding:0 7px; border-radius:11px; background:var(--purple-s); color:var(--purple); font-size:12px; font-weight:700; }
.pf-pod { display:grid; grid-template-columns:repeat(3, minmax(0,1fr)); gap:10px; align-items:end; margin-bottom:14px; }
.pf-pod .p { background:var(--surface); border:1px solid var(--border3); border-radius:14px; padding:14px 10px 12px; text-align:center; display:flex; flex-direction:column; align-items:center; gap:6px; min-width:0; }
.pf-pod .p.first { padding-top:20px; border-color:rgba(245,197,66,.35); background:linear-gradient(180deg, rgba(245,197,66,.08), var(--surface) 60%); }
@media (max-width: 1100px) { .pf-wrap.has { grid-template-columns:minmax(0,1fr); } .pf-side { display:none; } }
@media (max-width: 768px) {
  .pf-meta .hide-m { display:none; }
  .pf-pod { gap:6px; }
  .pf-pod .p { padding:10px 6px; }
}
`;

function UserDetail({ u, onClose, onEdit }) {
  const stats = [
    ['Level', 'L' + (u.level ?? 0), 'var(--purple)', 'level'],
    ['XP', fmtNum(u.xp), 'var(--warning)', 'star'],
    ['Puan', fmtNum(u.points), 'var(--link)', 'target'],
    ['Coin', fmtNum(u.coins), '#f5c542', 'coin'],
    ['Seri', fmtNum(u.streak), '#ff8a4c', 'fire'],
    ['Doğru', fmtNum(u.total_correct), 'var(--success)', 'checkCircle'],
  ];
  return (
    <>
      <div className="flex-between" style={{ marginBottom: 12 }}>
        <span style={{ fontWeight: 600, color: 'var(--link)' }}>Profil</span>
        <button className="btn btn-icon" onClick={onClose} title="Kapat"><Ic n="close" s={20} /></button>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', marginBottom: 16 }}>
        <Avatar id={u.telegram_id} name={u.full_name || '?'} size={84} />
        <div style={{ fontSize: 18, fontWeight: 700, marginTop: 10 }}>{u.full_name || '—'}</div>
        {u.username && <div style={{ color: 'var(--link)', fontSize: 14 }}>@{u.username}</div>}
        <div className="text-muted text-sm">ID {u.telegram_id}</div>
      </div>
      <div className="kv-grid keep-grid" style={{ gridTemplateColumns: 'repeat(3, minmax(0,1fr))', marginBottom: 16 }}>
        {stats.map(([l, v, c, ic]) => (
          <div key={l} className="kv" style={{ textAlign: 'center', padding: '10px 6px' }}>
            <Ic n={ic} s={18} c={c} style={{ margin: '0 auto 4px' }} />
            <div className="v" style={{ color: c }}>{v}</div>
            <div className="k" style={{ marginBottom: 0 }}>{l}</div>
          </div>
        ))}
      </div>
      {u.custom_fields && Object.keys(u.custom_fields).length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text2)', marginBottom: 8 }}>Özel alanlar</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {Object.entries(u.custom_fields).map(([k, v]) => (
              <div key={k} className="kv"><div className="k">{k}</div><div className="v" style={{ fontWeight: 500 }}>{String(v)}</div></div>
            ))}
          </div>
        </div>
      )}
      {u.achievements?.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text2)', marginBottom: 8 }}>Başarılar</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {u.achievements.map(a => <span key={a.id} className="chip" style={{ background: 'rgba(245,197,66,.14)', color: '#f5c542' }}><Ic n="trophy" s={13} />{a.achievement_name}</span>)}
          </div>
        </div>
      )}
      <div className="text-muted text-sm" style={{ marginBottom: 12 }}>
        {u.created_at && <>Kayıt: {new Date(u.created_at).toLocaleDateString('tr-TR')} · </>}
        {u.last_seen && <>Son görülme: {new Date(u.last_seen).toLocaleString('tr-TR', { dateStyle: 'short', timeStyle: 'short' })}</>}
      </div>
      <button className="btn btn-teal w-full" onClick={() => onEdit(u)}><Ic n="edit" s={17} />Profili düzenle</button>
    </>
  );
}

export default function Profiles() {
  const [bots, setBots] = useState([]);
  const [selectedBot, setSelectedBot] = useState(null);
  const [tab, setTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [leaderboard, setLeaderboard] = useState([]);
  const [levels, setLevels] = useState([]);
  const [stats, setStats] = useState(null);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('xp');
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetail, setUserDetail] = useState(null);
  const [lbField, setLbField] = useState('xp');
  const [narrow, setNarrow] = useState(() => window.innerWidth <= 1100);

  const [showLevelForm, setShowLevelForm] = useState(false);
  const [levelForm, setLevelForm] = useState(EMPTY_LEVEL);
  const [saving, setSaving] = useState(false);
  const [applying, setApplying] = useState('');
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [toastEl, toast] = useToast();

  useEffect(() => { const h = () => setNarrow(window.innerWidth <= 1100); window.addEventListener('resize', h); return () => window.removeEventListener('resize', h); }, []);
  useEffect(() => {
    api.get('/bots').then(r => { setBots(r.data || []); if (r.data?.length) setSelectedBot(r.data[0]); }).catch(() => {});
  }, []);
  useEffect(() => { if (selectedBot) loadData(); }, [selectedBot, tab, sort, lbField]);

  async function loadData() {
    setLoading(true);
    try {
      if (tab === 'users') {
        const [res, statsRes] = await Promise.all([
          api.get(`/profiles/${selectedBot.id}/users`, { params: { sort, search, limit: 50 } }),
          api.get(`/profiles/${selectedBot.id}/stats`),
        ]);
        setUsers(res.data?.users || []); setTotal(res.data?.total || 0);
        setStats(statsRes.data || null);
      } else if (tab === 'leaderboard') {
        const res = await api.get(`/profiles/${selectedBot.id}/leaderboard`, { params: { field: lbField, limit: 20 } });
        setLeaderboard(res.data || []);
      } else if (tab === 'levels') {
        const res = await api.get(`/profiles/${selectedBot.id}/levels`);
        setLevels(res.data || []);
      }
    } catch (e) { /* noop */ }
    setLoading(false);
  }

  async function searchUsers(e) {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.get(`/profiles/${selectedBot.id}/users`, { params: { sort, search, limit: 50 } });
      setUsers(res.data?.users || []); setTotal(res.data?.total || 0);
    } catch (err) { /* noop */ }
    setLoading(false);
  }

  async function loadUserDetail(telegramId) {
    try {
      const res = await api.get(`/profiles/${selectedBot.id}/users/${telegramId}`);
      setUserDetail(res.data); setSelectedUser(telegramId);
    } catch (e) { toast('Profil yüklenemedi', 'err'); }
  }

  async function saveLevel(e) {
    e.preventDefault(); setSaving(true);
    try {
      await api.post(`/profiles/${selectedBot.id}/levels`, levelForm);
      setShowLevelForm(false);
      setLevelForm(EMPTY_LEVEL);
      toast('Level eklendi');
      loadData();
    } catch (err) { toast(err.response?.data?.error || 'Hata', 'err'); }
    setSaving(false);
  }

  async function deleteLevel(id) {
    if (!window.confirm('Bu level silinsin mi?')) return;
    try { await api.delete(`/profiles/${selectedBot.id}/levels/${id}`); toast('Silindi'); loadData(); } catch (e) { toast('Silinemedi', 'err'); }
  }

  async function applyTemplate(tpl) {
    setApplying(tpl.name);
    try {
      for (const l of tpl.levels) await api.post(`/profiles/${selectedBot.id}/levels`, l);
      toast(`"${tpl.name}" şablonu uygulandı`);
    } catch (e) { toast('Şablon uygulanamadı', 'err'); }
    setApplying('');
    loadData();
  }

  function openEdit(u) {
    setEditUser(u);
    setEditForm({ xp: u.xp, level: u.level, points: u.points, coins: u.coins, streak: u.streak });
  }

  async function saveEditUser(e) {
    e.preventDefault(); setSaving(true);
    try {
      await api.patch(`/profiles/${selectedBot.id}/users/${editUser.telegram_id}`, editForm);
      const id = editUser.telegram_id;
      setEditUser(null);
      toast('Profil güncellendi');
      loadData();
      if (selectedUser === id) loadUserDetail(id);
    } catch (err) { toast(err.response?.data?.error || 'Hata', 'err'); }
    setSaving(false);
  }

  function pickBot(b) { setSelectedBot(b); setSelectedUser(null); setUserDetail(null); }
  const closeDetail = () => { setSelectedUser(null); setUserDetail(null); };
  const lvlSorted = levels.slice().sort((a, b) => a.level - b.level);
  const maxXp = Math.max(1, ...lvlSorted.map(l => l.required_xp || 0));
  const podium = leaderboard.slice(0, 3);

  return (
    <div className="page-enter">
      <style>{CSS}</style>
      {toastEl}
      <PageHeader
        title="Profil sistemi"
        subtitle="Kullanıcı profilleri, XP, level ve sıralamalar"
        actions={tab === 'levels' ? <button className="btn btn-primary" onClick={() => { setLevelForm({ ...EMPTY_LEVEL, level: (lvlSorted[lvlSorted.length - 1]?.level || 0) + 1 }); setShowLevelForm(true); }}><Ic n="plus" s={18} />Level ekle</button> : null}
        botSelector={<BotSelector bots={bots} selected={selectedBot} onSelect={pickBot} />}
      />

      <div className="tabs" style={{ marginBottom: 16 }}>
        {[['users', 'Kullanıcılar', 'users'], ['leaderboard', 'Sıralama', 'trophy'], ['levels', 'Level sistemi', 'level']].map(([k, l, ic]) => (
          <button key={k} className={'tab' + (tab === k ? ' active' : '')} onClick={() => { setTab(k); if (k !== 'users') closeDetail(); }} style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}><Ic n={ic} s={17} />{l}</button>
        ))}
      </div>

      {/* ═══ KULLANICILAR ═══ */}
      {tab === 'users' && (
        <>
          {stats && (
            <div className="stats-grid" style={{ marginBottom: 14 }}>
              <StatCard icon="users" label="Toplam kullanıcı" value={fmtNum(stats.total_users)} />
              <StatCard icon="star" label="Ortalama XP" value={fmtNum(Math.round(stats.avg_xp || 0))} color="var(--warning)" bg="var(--warning-s)" />
              <StatCard icon="level" label="En yüksek level" value={'L' + (stats.max_level || 0)} color="var(--purple)" bg="var(--purple-s)" />
              <StatCard icon="checkCircle" label="Toplam doğru" value={fmtNum(stats.total_correct)} color="var(--success)" bg="var(--success-s)" />
              <StatCard icon="fire" label="Aktif (7 gün)" value={fmtNum(stats.active_week)} color="#ff8a4c" bg="rgba(255,138,76,.14)" />
            </div>
          )}

          <form onSubmit={searchUsers} style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 240px', display: 'flex', alignItems: 'center', gap: 8, background: 'var(--surface3)', borderRadius: 10, padding: '0 12px', minHeight: 40 }}>
              <Ic n="search" s={18} c="var(--text3)" />
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="İsim veya kullanıcı adı ara"
                style={{ flex: 1, background: 'none', border: 'none', outline: 'none', color: 'var(--text)', fontSize: 15, minWidth: 0, fontFamily: 'inherit' }} />
              {search && <button type="button" className="btn btn-icon" style={{ width: 28, minHeight: 28 }} onClick={() => setSearch('')}><Ic n="close" s={16} /></button>}
            </div>
            <select className="input" style={{ width: 'auto', flex: '0 1 170px' }} value={sort} onChange={e => setSort(e.target.value)} title="Sıralama">
              {Object.entries(FIELD_LABELS).map(([k, v]) => <option key={k} value={k}>Sırala: {v}</option>)}
            </select>
            <button type="submit" className="btn btn-primary">Ara</button>
          </form>

          <div className={'pf-wrap' + (userDetail && !narrow ? ' has' : '')}>
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              {loading ? <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><Spinner /></div>
                : !users.length ? <div className="empty-state"><div className="icon"><Ic n="users" s={44} /></div><p>Kullanıcı bulunamadı</p></div>
                  : (
                    <div className="list">
                      {users.map(u => (
                        <div key={u.id || u.telegram_id} className={'list-row click' + (selectedUser === u.telegram_id ? ' on' : '')} onClick={() => loadUserDetail(u.telegram_id)}>
                          <Avatar id={u.telegram_id} name={u.full_name || '?'} size={44} />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div className="flex-center gap-8" style={{ minWidth: 0 }}>
                              <span className="truncate" style={{ fontWeight: 600 }}>{u.full_name || '—'}</span>
                              <span className="pf-lvl">L{u.level}</span>
                            </div>
                            <div className="truncate text-muted" style={{ fontSize: 13 }}>
                              {u.username ? '@' + u.username + ' · ' : ''}{u.last_seen ? new Date(u.last_seen).toLocaleDateString('tr-TR') : ''}
                            </div>
                          </div>
                          <div className="pf-meta">
                            <div className="m"><b style={{ color: 'var(--warning)' }}>{fmtNum(u.xp)}</b><span>XP</span></div>
                            <div className="m hide-m"><b>{fmtNum(u.points)}</b><span>Puan</span></div>
                            <div className="m hide-m"><b style={{ color: '#f5c542' }}>{fmtNum(u.coins)}</b><span>Coin</span></div>
                            <div className="m hide-m"><b style={{ color: '#ff8a4c' }}>{fmtNum(u.streak)}</b><span>Seri</span></div>
                          </div>
                          <button className="btn btn-icon" title="Düzenle" onClick={e => { e.stopPropagation(); openEdit(u); }}><Ic n="edit" s={18} /></button>
                        </div>
                      ))}
                    </div>
                  )}
              {!loading && users.length > 0 && <div style={{ padding: '10px 14px', fontSize: 12.5, color: 'var(--text3)', borderTop: '1px solid var(--border3)' }}>{fmtNum(total)} kullanıcı{total > users.length ? ` · ilk ${users.length} gösteriliyor` : ''}</div>}
            </div>
            {userDetail && !narrow && <div className="card pf-side"><UserDetail u={userDetail} onClose={closeDetail} onEdit={openEdit} /></div>}
          </div>
        </>
      )}

      {/* ═══ SIRALAMA ═══ */}
      {tab === 'leaderboard' && (
        <>
          <div className="seg" style={{ marginBottom: 14, flexWrap: 'wrap', overflow: 'visible' }}>
            {LB_FIELDS.map(k => <button key={k} className={lbField === k ? 'on' : ''} onClick={() => setLbField(k)}>{FIELD_LABELS[k]}</button>)}
          </div>
          {loading ? <div className="card" style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><Spinner /></div>
            : !leaderboard.length ? <div className="card"><div className="empty-state"><div className="icon"><Ic n="trophy" s={44} /></div><p>Henüz veri yok</p></div></div>
              : (
                <>
                  {podium.length >= 3 && (
                    <div className="pf-pod">
                      {[1, 0, 2].map(i => {
                        const u = podium[i];
                        return (
                          <div key={u.telegram_id} className={'p' + (i === 0 ? ' first' : '')} style={{ cursor: 'pointer' }} onClick={() => { setTab('users'); loadUserDetail(u.telegram_id); }}>
                            <div style={{ position: 'relative' }}>
                              <Avatar id={u.telegram_id} name={u.full_name || '?'} size={i === 0 ? 64 : 52} ring={MEDAL[i]} />
                              <span style={{ position: 'absolute', bottom: -6, left: '50%', transform: 'translateX(-50%)', background: MEDAL[i], color: '#17212b', fontSize: 12, fontWeight: 800, width: 22, height: 22, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 0 0 2px var(--surface)' }}>{i + 1}</span>
                            </div>
                            <div className="truncate" style={{ fontWeight: 600, fontSize: 14, maxWidth: '100%', marginTop: 4 }}>{u.full_name || '—'}</div>
                            <div style={{ fontSize: i === 0 ? 20 : 17, fontWeight: 800, color: MEDAL[i], lineHeight: 1 }}>{fmtNum(u[lbField])}</div>
                            <div className="text-muted" style={{ fontSize: 11.5 }}>{FIELD_LABELS[lbField]} · L{u.level}</div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                    <div className="list">
                      {leaderboard.slice(podium.length >= 3 ? 3 : 0).map((u, idx) => {
                        const i = idx + (podium.length >= 3 ? 3 : 0);
                        const top = Number(leaderboard[0]?.[lbField]) || 1;
                        return (
                          <div key={u.telegram_id} className="list-row">
                            <div style={{ width: 26, textAlign: 'center', fontWeight: 700, color: i < 3 ? MEDAL[i] : 'var(--text3)' }}>{i + 1}</div>
                            <Avatar id={u.telegram_id} name={u.full_name || '?'} size={40} />
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div className="flex-between" style={{ gap: 8 }}>
                                <span className="truncate" style={{ fontWeight: 600 }}>{u.full_name || '—'}</span>
                                <span style={{ fontWeight: 700, color: 'var(--warning)', flexShrink: 0 }}>{fmtNum(u[lbField])} <span className="text-muted" style={{ fontWeight: 500, fontSize: 12 }}>{FIELD_LABELS[lbField]}</span></span>
                              </div>
                              <div className="flex-center gap-8" style={{ marginTop: 5 }}>
                                <Bar pct={(Number(u[lbField]) / top) * 100} color="var(--accent)" h={4} />
                                <span className="text-muted" style={{ fontSize: 12, flexShrink: 0 }}>L{u.level}{lbField !== 'xp' ? ` · ${fmtNum(u.xp)} XP` : ''}</span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </>
              )}
        </>
      )}

      {/* ═══ LEVEL SİSTEMİ ═══ */}
      {tab === 'levels' && (
        loading ? <div className="card" style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><Spinner /></div> : (
          <>
            {!levels.length && (
              <div className="card" style={{ marginBottom: 14 }}>
                <div className="flex-center gap-8" style={{ fontWeight: 600, marginBottom: 4 }}><Ic n="sparkle" s={18} c="var(--link)" />Hazır şablonla başla</div>
                <div className="text-muted text-sm" style={{ marginBottom: 12 }}>Tek dokunuşla 5 seviyelik bir sistem kurulur, sonra düzenleyebilirsiniz.</div>
                <div className="grid-3">
                  {TEMPLATES.map(tpl => (
                    <button key={tpl.name} className="list-row click" disabled={!!applying} onClick={() => applyTemplate(tpl)} style={{ border: '1px solid var(--border3)', borderRadius: 12, background: 'var(--surface2)', color: 'inherit', font: 'inherit', textAlign: 'left' }}>
                      <div className="ic-circle" style={{ background: 'var(--accent-s)', color: 'var(--link)' }}><Ic n={tpl.icon} s={20} /></div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600 }}>{tpl.name}</div>
                        <div className="truncate text-muted" style={{ fontSize: 13 }}>{tpl.levels.map(l => l.badge).join(' ')} · {tpl.levels.length} level</div>
                      </div>
                      {applying === tpl.name ? <Spinner size={20} /> : <Ic n="chevR" s={18} c="var(--text3)" />}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {levels.length > 0 ? (
              <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                <div className="sec-title"><span className="flex-center gap-8"><Ic n="level" s={18} c="var(--purple)" />{levels.length} seviye</span><span className="text-muted text-sm" style={{ fontWeight: 500 }}>XP'ye göre sıralı</span></div>
                <div className="list">
                  {lvlSorted.map(l => (
                    <div key={l.id} className="list-row" style={{ alignItems: 'flex-start' }}>
                      <div style={{ width: 44, height: 44, borderRadius: 12, background: 'var(--surface3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, flexShrink: 0 }}>{l.badge || '⭐'}</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="flex-center gap-8" style={{ flexWrap: 'wrap' }}>
                          <span style={{ fontWeight: 700 }}>{l.name}</span>
                          <span className="pf-lvl">Level {l.level}</span>
                        </div>
                        <div className="flex-center gap-8" style={{ margin: '6px 0 2px' }}>
                          <Bar pct={((l.required_xp || 0) / maxXp) * 100} color="var(--warning)" h={5} />
                          <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--warning)', flexShrink: 0 }}>{fmtNum(l.required_xp)} XP</span>
                        </div>
                        {l.reward_message && <div className="text-muted" style={{ fontSize: 13, marginTop: 4, display: 'flex', gap: 6 }}><Ic n="chat" s={15} />{l.reward_message}</div>}
                      </div>
                      <button className="btn btn-icon" title="Sil" style={{ color: 'var(--danger)' }} onClick={() => deleteLevel(l.id)}><Ic n="trash" s={18} /></button>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="card"><div className="empty-state"><div className="icon"><Ic n="level" s={44} /></div><p>Level tanımlanmamış</p></div></div>
            )}
          </>
        )
      )}

      {/* Mobil / dar ekran: profil detayı */}
      {userDetail && narrow && tab === 'users' && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && closeDetail()}>
          <div className="modal" style={{ maxWidth: 480 }}><UserDetail u={userDetail} onClose={closeDetail} onEdit={u => { closeDetail(); openEdit(u); }} /></div>
        </div>
      )}

      {/* Level ekle */}
      {showLevelForm && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowLevelForm(false)}>
          <div className="modal">
            <h3 className="flex-center gap-8"><Ic n="level" s={20} c="var(--purple)" />Level ekle</h3>
            <form onSubmit={saveLevel}>
              <div className="form-3 keep-grid" style={{ gridTemplateColumns: '90px minmax(0,1fr) 90px' }}>
                <div className="input-group"><label>Level</label><input type="number" className="input" value={levelForm.level} onChange={e => setLevelForm({ ...levelForm, level: parseInt(e.target.value, 10) || 0 })} required /></div>
                <div className="input-group"><label>Ad</label><input className="input" value={levelForm.name} onChange={e => setLevelForm({ ...levelForm, name: e.target.value })} placeholder="Örn: Efsane" required /></div>
                <div className="input-group"><label>Rozet</label><input className="input" value={levelForm.badge} onChange={e => setLevelForm({ ...levelForm, badge: e.target.value })} placeholder="⭐" style={{ textAlign: 'center' }} /></div>
              </div>
              <div className="input-group"><label>Gerekli XP</label><input type="number" className="input" value={levelForm.required_xp} onChange={e => setLevelForm({ ...levelForm, required_xp: parseInt(e.target.value, 10) || 0 })} required /></div>
              <div className="input-group" style={{ marginBottom: 18 }}>
                <label>Level atlama mesajı (opsiyonel)</label>
                <textarea className="input" value={levelForm.reward_message} onChange={e => setLevelForm({ ...levelForm, reward_message: e.target.value })} placeholder="Tebrikler! Bu seviyeye ulaştın…" style={{ minHeight: 64 }} />
              </div>
              <div className="flex gap-8" style={{ justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-ghost" onClick={() => setShowLevelForm(false)}>İptal</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Kaydediliyor…' : 'Kaydet'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Profil düzenle */}
      {editUser && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setEditUser(null)}>
          <div className="modal">
            <div className="flex-center gap-12" style={{ marginBottom: 16 }}>
              <Avatar id={editUser.telegram_id} name={editUser.full_name || '?'} size={44} />
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 16 }} className="truncate">{editUser.full_name || '—'}</div>
                <div className="text-muted text-sm">Profil değerlerini düzenle</div>
              </div>
            </div>
            <form onSubmit={saveEditUser}>
              <div className="form-2 keep-grid" style={{ gridTemplateColumns: 'repeat(2, minmax(0,1fr))' }}>
                {[['xp', 'XP', 0], ['level', 'Level', 1], ['points', 'Puan', 0], ['coins', 'Coin', 0], ['streak', 'Seri', 0]].map(([k, l, d]) => (
                  <div key={k} className="input-group">
                    <label>{l}</label>
                    <input type="number" className="input" value={editForm[k] ?? d} onChange={e => setEditForm({ ...editForm, [k]: parseInt(e.target.value, 10) || 0 })} />
                  </div>
                ))}
              </div>
              <div className="flex gap-8" style={{ justifyContent: 'flex-end', marginTop: 4 }}>
                <button type="button" className="btn btn-ghost" onClick={() => setEditUser(null)}>İptal</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Kaydediliyor…' : 'Kaydet'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
